import { CARD_W } from "@webscan/constants.js";
import { boxPixels, MODERN_REGIONS, type Region } from "@webscan/ocrprep.js";
import { homographyToQuad } from "@webscan/warp.js";
import {
  applyHomography,
  windowHomography,
  type Homography,
  type SourceWindow,
} from "../shared/geometry";
import type { GlSampler } from "./warp-gl";
import { pixelSize, type PixelSource } from "./camera-frame";

export const COLLECTOR_MIN_HEIGHT = 1000;
export const COLLECTOR_SCALE = 8;
export const COLLECTOR_MAX_WIDTH = Math.ceil(CARD_W * 0.52) * COLLECTOR_SCALE;
export interface Strip {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}
export interface CollectorStrips {
  collector: Strip;
  setInfo: Strip;
}
export interface CollectorEvidence {
  height: number;
  upright: CollectorStrips | null;
  flipped: CollectorStrips | null;
  /** Frozen sensor crops, expanded only for the chosen sample and orientation. */
  sample?: (flipped: boolean) => CollectorStrips | null;
}

interface SensorCrop {
  rgba: Uint8ClampedArray;
  sw: number;
  sh: number;
  sx: number;
  sy: number;
  geometry: { width: number; height: number; matrix: Homography };
}

/** Sensor pixels, independent of preview zoom, overscan and desk-view scale. */
export function sensorQuad(
  window: SourceWindow,
  quad: Float32Array,
  flipped = false,
): Float32Array {
  const h = windowHomography(window);
  const out = new Float32Array(8);
  for (let k = 0; k < 4; k++) {
    const source = ((k + (flipped ? 2 : 0)) % 4) * 2;
    const [x, y] = applyHomography(h, quad[source], quad[source + 1]);
    out[k * 2] = x;
    out[k * 2 + 1] = y;
  }
  return out;
}

export function sensorCardHeight(window: SourceWindow, quad: Float32Array): number {
  const q = sensorQuad(window, quad);
  const height = Math.min(
    Math.hypot(q[6] - q[0], q[7] - q[1]),
    Math.hypot(q[4] - q[2], q[5] - q[3]),
  );
  return Number.isFinite(height) ? height : 0;
}

/** Map strip pixel centres directly through the canonical homography to the sensor. */
export function stripGeometry(quad: Float32Array, region: Region, scale = COLLECTOR_SCALE) {
  const h = homographyToQuad(quad);
  if (!h) return null;
  const [x0, y0, x1, y1] = boxPixels(region);
  const x = x0 + 0.5 / scale - 0.5;
  const y = y0 + 0.5 / scale - 0.5;
  const matrix: Homography = [
    h[0] / scale,
    h[1] / scale,
    h[0] * x + h[1] * y + h[2],
    h[3] / scale,
    h[4] / scale,
    h[3] * x + h[4] * y + h[5],
    h[6] / scale,
    h[7] / scale,
    h[6] * x + h[7] * y + h[8],
  ];
  return { width: (x1 - x0) * scale, height: (y1 - y0) * scale, matrix };
}

/**
 * Only the two single-line boxes are warped at 8x, never a 3904x5440 full card.
 * The CPU fallback reads a bounding sensor crop without downsampling first. Both
 * orientations are captured before the initial read; retries know the orientation.
 */
export class CollectorSampler {
  private canvas: HTMLCanvasElement | null = null;

  /**
   * Freeze small, native-resolution crops before the camera advances. On the CPU path,
   * defer the expensive 8x warp until retrieval has chosen a sample and orientation and
   * actually needs OCR. The two-frame sharpness comparison used to expand eight strips
   * before recognition even started, though at most two could contribute to the answer.
   * The closure owns only pixel buffers, never a live video or retained camera frame.
   */
  capture(
    video: PixelSource,
    window: SourceWindow,
    quad: Float32Array,
    bothOrientations: boolean,
    gl: GlSampler | null = null,
  ): CollectorEvidence {
    const height = sensorCardHeight(window, quad);
    if (gl) {
      // The shared texture belongs to the current frame. Its cheap GPU warps must
      // finish now, before the next detection replaces that texture.
      return {
        height,
        upright: this.read(video, window, quad, false, gl),
        flipped: bothOrientations ? this.read(video, window, quad, true, gl) : null,
      };
    }
    const crops = [false, ...(bothOrientations ? [true] : [])].map((flipped) => {
      const inSensor = sensorQuad(window, quad, flipped);
      return [MODERN_REGIONS.collector, MODERN_REGIONS.set_info].map((region) => {
        const geometry = stripGeometry(inSensor, region);
        return geometry ? this.snapshotRegion(video, geometry) : null;
      });
    });
    const cached: Array<CollectorStrips | null | undefined> = [];
    return {
      height,
      upright: null,
      flipped: null,
      sample: (flipped) => {
        const slot = flipped ? 1 : 0;
        if (cached[slot] !== undefined) return cached[slot];
        const [collector, setInfo] = crops[slot] ?? [];
        cached[slot] =
          collector && setInfo
            ? { collector: warpSensorCrop(collector), setInfo: warpSensorCrop(setInfo) }
            : null;
        return cached[slot];
      },
    };
  }

  read(
    video: PixelSource,
    window: SourceWindow,
    quad: Float32Array,
    flipped: boolean,
    gl: GlSampler | null = null,
  ): CollectorStrips | null {
    const inSensor = sensorQuad(window, quad, flipped);
    const sample = (region: Region): Strip | null => {
      const geometry = stripGeometry(inSensor, region);
      if (!geometry) return null;
      const gpu = gl?.warpRegion(video, geometry.width, geometry.height, geometry.matrix);
      if (gpu) return { ...geometry, data: new Uint8ClampedArray(gpu) };
      const crop = this.snapshotRegion(video, geometry);
      return crop ? warpSensorCrop(crop) : null;
    };
    const collector = sample(MODERN_REGIONS.collector);
    const setInfo = sample(MODERN_REGIONS.set_info);
    return collector && setInfo ? { collector, setInfo } : null;
  }

  private snapshotRegion(
    video: PixelSource,
    geometry: { width: number; height: number; matrix: Homography },
  ): SensorCrop | null {
    const sensor = pixelSize(video);
    const { width, height, matrix } = geometry;
    const corners = [
      [0, 0],
      [width - 1, 0],
      [width - 1, height - 1],
      [0, height - 1],
    ].map(([x, y]) => applyHomography(matrix, x, y));
    if (corners.some((p) => p.some((v) => !Number.isFinite(v)))) return null;
    const sx = Math.max(0, Math.floor(Math.min(...corners.map((p) => p[0]))) - 1);
    const sy = Math.max(0, Math.floor(Math.min(...corners.map((p) => p[1]))) - 1);
    const right = Math.min(sensor.width, Math.ceil(Math.max(...corners.map((p) => p[0]))) + 2);
    const bottom = Math.min(sensor.height, Math.ceil(Math.max(...corners.map((p) => p[1]))) + 2);
    const sw = right - sx,
      sh = bottom - sy;
    if (sw < 2 || sh < 2) return null;
    try {
      this.canvas ??= document.createElement("canvas");
      this.canvas.width = sw;
      this.canvas.height = sh;
      const context = this.canvas.getContext("2d", { willReadFrequently: false });
      if (!context) return null;
      context.drawImage(video, sx, sy, sw, sh, 0, 0, sw, sh);
      const rgba = context.getImageData(0, 0, sw, sh).data;
      return { rgba, sw, sh, sx, sy, geometry };
    } catch {
      return null;
    }
  }

  dispose(): void {
    if (this.canvas) this.canvas.width = this.canvas.height = 0;
    this.canvas = null;
  }
}

/** Same homography, clamping and byte rounding for immediate and deferred warps. */
function warpSensorCrop({ rgba, sw, sh, sx, sy, geometry }: SensorCrop): Strip {
  const { width, height, matrix } = geometry;
  const data = new Uint8ClampedArray(width * height * 4);
  for (let y = 0; y < height; y++)
    for (let x = 0; x < width; x++) {
      const p = applyHomography(matrix, x, y);
      const px = Math.max(0, Math.min(sw - 1.001, p[0] - sx));
      const py = Math.max(0, Math.min(sh - 1.001, p[1] - sy));
      const ix = Math.floor(px),
        iy = Math.floor(py),
        fx = px - ix,
        fy = py - iy;
      const start = (iy * sw + ix) * 4,
        out = (y * width + x) * 4;
      for (let c = 0; c < 3; c++)
        data[out + c] =
          rgba[start + c] * (1 - fx) * (1 - fy) +
          rgba[start + 4 + c] * fx * (1 - fy) +
          rgba[start + sw * 4 + c] * (1 - fx) * fy +
          rgba[start + sw * 4 + 4 + c] * fx * fy;
      data[out + 3] = 255;
    }
  return { data, width, height };
}
